import React, { useState } from "react";
import Date from "../../super_dashboard/components/Date";
import CancelIcon from "@mui/icons-material/Cancel";

const CATEGORIES = ["Personnel", "Facility", "Marketing", "Technology", "Inventory", "Compliance", "Others"];
const TYPES = ["daily", "weekly", "monthly", "yearly"];
const EMPTY = { start_date: "", end_date: "", category: "", type: "", min_amount: "", max_amount: "" };

// Date range (start_date / end_date, "Y-M-D") plus category, type and amount range
const ExpensesFilter = ({ isOpen, onClose, filteredData, onApply, onClear }) => {
  const [formData, setFormData] = useState({ ...EMPTY, ...filteredData });

  if (!isOpen) return null;

  const handleDateChange = (year, month, day, type) => {
    setFormData((prevData) => ({
      ...prevData,
      [type]: `${year}-${month}-${day}`,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // The API only filters dates when both are sent, so a lone date is not a valid filter
  const hasOneDate = Boolean(formData.start_date) !== Boolean(formData.end_date);
  const hasAnyFilter = Object.values(formData).some(Boolean);
  const canSearch = hasAnyFilter && !hasOneDate;
  const inputClass =
    "w-full rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 shadow focus:outline-none focus:ring-2 focus:ring-green-400";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 bg-black/60 backdrop-blur">
      <div className="bg-white rounded shadow-lg w-full max-w-md md:max-w-lg p-3 md:p-6 overflow-y-auto max-h-[88vh] border border-neutral-200 relative ring-1 ring-green-100">
        <div className="flex justify-between items-center border-b pb-2 mb-4">
          <h2 className="text-xl md:text-2xl font-extrabold text-neutral-800 tracking-tight px-1">
            Filters
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-neutral-200 transition"
            aria-label="Cancel filter"
          >
            <CancelIcon
              sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }}
              className="text-gray-500 hover:text-green-600 transition-colors"
            />
          </button>
        </div>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label htmlFor="start-date" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                Start Date
              </label>
              <div className="rounded-lg bg-gray-50 py-2 focus-within:ring-2 focus-within:ring-green-400 shadow transition-all duration-150">
                <Date
                  onDateChange={handleDateChange}
                  type="start_date"
                  id="start-date"
                  className="w-full"
                  existingDate={formData.start_date}
                />
              </div>
            </div>
            <div>
              <label htmlFor="end-date" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                End Date
              </label>
              <div className="rounded-lg bg-gray-50 py-2 focus-within:ring-2 focus-within:ring-green-400 shadow transition-all duration-150">
                <Date
                  onDateChange={handleDateChange}
                  type="end_date"
                  id="end-date"
                  className="w-full"
                  existingDate={formData.end_date}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                Category
              </label>
              <select id="category" name="category" value={formData.category} onChange={handleInputChange} className={inputClass}>
                <option value="">All categories</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="type" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                Type
              </label>
              <select id="type" name="type" value={formData.type} onChange={handleInputChange} className={`${inputClass} capitalize`}>
                <option value="">All types</option>
                {TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="min_amount" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                Min Amount
              </label>
              <input id="min_amount" type="number" min="0" name="min_amount" value={formData.min_amount} onChange={handleInputChange} placeholder="e.g. 1000" className={inputClass} />
            </div>
            <div>
              <label htmlFor="max_amount" className="block text-sm md:text-base font-semibold mb-1 text-gray-800">
                Max Amount
              </label>
              <input id="max_amount" type="number" min="0" name="max_amount" value={formData.max_amount} onChange={handleInputChange} placeholder="e.g. 50000" className={inputClass} />
            </div>
          </div>

          {hasOneDate && (
            <p className="text-sm text-red-600">Pick both a start and an end date.</p>
          )}

          <div className="flex flex-col md:flex-row justify-center md:justify-end gap-2 mt-6">
            {filteredData && (
              <button
                onClick={() => {
                  setFormData(EMPTY);
                  onClear();
                }}
                className="border border-neutral-200 bg-white px-4 py-1.5 md:px-6 md:py-2 rounded-full font-medium text-gray-700 hover:bg-neutral-100 transition shadow"
              >
                Clear
              </button>
            )}
            <button
              onClick={onClose}
              className="border border-neutral-200 bg-white px-4 py-1.5 md:px-6 md:py-2 rounded-full font-medium text-gray-700 hover:bg-neutral-100 hover:border-green-300 focus:ring-1 focus:ring-green-400 focus:outline-none transition shadow"
            >
              Cancel
            </button>
            <button
              onClick={() => onApply(formData)}
              disabled={!canSearch}
              className="bg-green-600 text-white px-6 py-1.5 md:px-8 md:py-2 rounded-full font-bold shadow hover:bg-green-700 focus:ring-1 focus:ring-green-500 focus:outline-none transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesFilter;
